import RefreshIcon from "@mui/icons-material/Refresh";
import { Alert, Box, Button, Container, Grid, Typography } from "@mui/material";
import React, { useState } from "react";

import { BillingOverviewCards } from "./components/BillingOverviewCards";
import { BillingSubscriptionsTable } from "./components/BillingSubscriptionsTable";
import { CreditCouponCard } from "./components/CreditCouponCard";
import { useBillingSubscriptionsQuery } from "./hooks/useBillingSubscriptionsQuery";
import { useBillingMetricsQuery } from "./hooks/useBillingMetricsQuery";

import { TBillingOrder, TBillingOrderBy } from "./interfaces/billingSubscriptions.interface";
import { BillingPlansInfoSection } from "./components/BillingPlansInfoSection";
import { EProductSection } from "src/commons/interfaces/product";
import { useContactCenterUsageSummaryQuery } from "./hooks/useContactCenterUsageSummaryQuery";

export const BillingSubscriptionsLayout: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [search, setSearch] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [order, setOrder] = useState<TBillingOrder>("DESC");
  const [orderBy, setOrderBy] = useState<TBillingOrderBy>("expDate");

  const actualStatus = statusFilter === "all" ? undefined : statusFilter;

  const { data, isLoading, isError, error, refetch } = useBillingSubscriptionsQuery({
    page,
    pageSize,
    order,
    orderBy,
    status: actualStatus,
    clientName: search || undefined,
  });

  const { data: metrics, isLoading: isMetricsLoading, refetch: refetchMetrics } = useBillingMetricsQuery();

  const handleRefresh = () => {
    refetch();
    refetchMetrics();
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setPage(1);
  };

  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setPage(1);
  };

  const handleSortChange = (newOrderBy: TBillingOrderBy, newOrder: TBillingOrder) => {
    setOrderBy(newOrderBy);
    setOrder(newOrder);
    setPage(1);
  };

  const items = data?.items ?? [];
  const totalItems = data?.totalItems ?? 0;
  const totalPages = data?.totalPages ?? 0;

  return (
    <>
      <Box
        sx={{
          py: 2,
          px: 2,

          minHeight: "90vh",
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              mb: 4,
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "var(--fx-surface-text, #0F172A)",
                  letterSpacing: "-0.5px",
                }}
              >
                Subscriptions & Billing
              </Typography>
              <Typography variant="body2" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 500, mt: 0.5 }}>
                View company subscriptions and billing status
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{
                borderRadius: "12px",
                borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                color: "var(--fx-surface-textsecondary, #64748B)",
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--fx-surface-base, #FFFFFF)",
                "&:hover": {
                  borderColor: "var(--fx-surface-texttertiary, #94A3B8)",
                  bgcolor: "var(--fx-surface-muted, #F1F5F9)",
                },
              }}
            >
              Refresh Data
            </Button>
          </Box>

          {/* Error Alert if query fails */}
          {isError && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>
              Failed to load billing subscriptions. {(error as Error)?.message || "Please try again."}
            </Alert>
          )}

          {/* Top Grid: Overview Metrics (Left 65-70%) + Credit/Coupon (Right 30-35%) */}
          <Box sx={{ mb: 4 }}>
            <BillingOverviewCards metrics={metrics} isLoading={isMetricsLoading} />
          </Box>

          {/* Main Table Section */}
          <BillingSubscriptionsTable
            items={items}
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            totalPages={totalPages}
            isLoading={isLoading}
            search={search}
            statusFilter={statusFilter}
            order={order}
            orderBy={orderBy}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSearchChange={handleSearchChange}
            onStatusFilterChange={handleStatusFilterChange}
            onSortChange={handleSortChange}
          />
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <BillingPlansInfoSection
          section={EProductSection.FLOW_SUBSCRIPTION}
          title="Flow Subscription plans"
          description="Main subscription plans available for companies, including plan pricing, billing type, and included benefits."
        />

        <BillingPlansInfoSection
          section={EProductSection.FLOW_MOBILE}
          title="Flow Mobile plans"
          description="Mobile add-on plans available for companies, including base price, usage model, and included benefits."
        />
      </Container>
    </>
  );
};

export default BillingSubscriptionsLayout;
